// SURGE AURA BED (owner 2026-11-03: 'use their aura sound clips for when auras are activated, each Surge level more intense').
// The looping aura that sits under combat while a Surge level is active. Pure state machine (no WebAudio): src/audio/sfx.ts
// supplies the voice operations, so tests/auraBed.test.ts can drive it with a fake. It keeps exactly ONE loop voice:
//   enter(N)        transform into level N: the loop starts after N's intro (old loop, if any, crossfades out over XFADE)
//   sync(tier, mode) every frame: tier 0 = Surge ended (loop fades over END_FADE); mode = what the screen lets it do
// Re-entering the level already playing keeps its loop. Rapid toggles only ever replace the one voice. A level whose loop
// is not decoded yet (lazy load) or has no clip gets no voice; sync() retries it each frame (cheap: start returns -1).

/** what the current screen allows: live (in play), duck (level-up / boon / chest cards), mute (pause / settings / training
 *  panel / how to play: silent but kept, so it comes back on resume), off (title, results, death: stop) */
export type AuraMode = 'live' | 'duck' | 'mute' | 'off';
export const AURA_DUCK: Record<Exclude<AuraMode, 'off'>, number> = { live: 1, duck: 0.35, mute: 0 };
/** seconds: old loop out on a level change, loop out when Surge ends, hard stop (death / quit), new loop fade-in */
export const XFADE = 0.4, END_FADE = 0.6, STOP_FADE = 0.15, LOOP_FADE_IN = 0.8;

export interface AuraVoiceOps {
  /** start level `tier`'s loop at audio time `at` with a `fadeIn` s ramp; returns a handle, or -1 if it has no loop
   *  (not decoded yet, or no clip for that level) */
  start(tier: number, at: number, fadeIn: number): number;
  /** fade the voice out over `dur` s and free it */
  stop(handle: number, dur: number): void;
  /** bed duck level 0..1 (one gain shared by the voice) */
  duck(level: number): void;
}

export class AuraBed {
  /** the Surge level whose bed is wanted (0 = none) */
  tier = 0;
  /** the live loop voice (-1 = none) */
  handle = -1;
  private level = 1;
  constructor(private ops: AuraVoiceOps) {}
  /** transform into `tier` now; its loop starts at `loopAt` (after the intro). Returns true if a new loop was scheduled. */
  enter(tier: number, loopAt: number): boolean {
    if (tier <= 0) return false;
    if (tier === this.tier && this.handle >= 0) return false; // same level again: keep the bed
    if (this.handle >= 0) this.ops.stop(this.handle, XFADE);
    this.tier = tier; this.handle = this.ops.start(tier, loopAt, LOOP_FADE_IN);
    return this.handle >= 0;
  }
  /** per frame: the sim's current Surge level (0 = not in Surge) and what the screen allows. `now` = audio time, used
   *  when the level changed without a transform event (training tools, QA staging): its loop then starts at once. */
  sync(tier: number, mode: AuraMode, now: number): void {
    if (mode === 'off' || tier <= 0) {
      if (this.handle >= 0) this.ops.stop(this.handle, mode === 'off' ? STOP_FADE : END_FADE);
      this.handle = -1; this.tier = 0;
    } else if (tier !== this.tier) this.enter(tier, now);
    else if (this.handle < 0) this.handle = this.ops.start(tier, now, LOOP_FADE_IN); // its loop decoded late (lazy load)
    const lv = mode === 'off' ? 1 : AURA_DUCK[mode];
    if (lv !== this.level) { this.level = lv; this.ops.duck(lv); }
  }
  /** a loop is playing (or scheduled) */
  get active(): boolean { return this.handle >= 0; }
}
